<template>
  <ul
    v-if="cart?.items?.length"
    class="cart-totals"
    :class="{ 'cart-totals--order-page': isOrderPage }"
  >
    <li
      v-if="showSubtotalLine"
      class="cart-totals__row cart-totals__row--subtotal subtotal-line"
    >
      <span class="cart-totals__label label">{{ subtotalLabelText }}</span>
      <span class="cart-totals__value">
        <span class="cart-totals__amount subtotal">{{ subtotal }}</span>
        <span class="cart-totals__currency currency">&euro;</span>
      </span>
    </li>
    <li
      v-if="showShippingLine"
      class="cart-totals__row cart-totals__row--shipping shipping-price-total"
    >
      <span class="cart-totals__label label">{{ shippingLabelText }}</span>
      <span class="cart-totals__value">
        <span class="cart-totals__amount amount">{{ shippingPrice }}</span>
        <span class="cart-totals__currency currency">€</span>
      </span>
    </li>
    <li
      v-for="coupon in appliedCoupons"
      :key="coupon.code"
      class="cart-totals__row cart-totals__row--discount coupons"
    >
      <span class="cart-totals__label label">
        <span class="cart-totals__discount-label">{{ discountCodeLabel }}</span>
        <span class="cart-totals__discount-code">{{ coupon.code }}</span>
        <span
          v-if="coupon.discountAmount && coupon.isPercentage"
          class="cart-totals__discount-pct"
        >
          (-{{ coupon.discountAmount }}%)
        </span>
        <button
          type="button"
          name="deleteCoupon"
          class="cart-totals__remove delete-coupon-btn"
          @click.prevent="deleteCouponAction(coupon)"
        >
          {{ $t("labels.deleteCoupon") }}
        </button>
      </span>
      <span v-if="coupon.discountedAmount" class="cart-totals__value">
        <span class="cart-totals__amount amount cart-totals__amount--discount"
          >-{{ formatPrice(coupon.discountedAmount) }}</span
        >
        <span class="cart-totals__currency currency">€</span>
      </span>
    </li>
    <li class="cart-totals__row cart-totals__row--total total">
      <span class="cart-totals__label label">{{ totalLabelText }}</span>
      <span class="cart-totals__value">
        <span class="cart-totals__amount subtotal">{{ total }}</span>
        <span class="cart-totals__currency currency">&euro;</span>
      </span>
    </li>
  </ul>
</template>
<script>
import { useCartStore } from "@/store/cart";
import CouponService from "@/services/coupon";
import Price from "@/services/price";
import {
  getOrderPageContextVersion,
  isOrderPageContext,
} from "@/services/orderPageScope";

export default {
  name: "CartTotals",
  props: {
    showSubtotalIfFreeShipping: {
      type: Boolean,
      default: true,
    },
    subtotalLabel: {
      type: String,
      default: "",
    },
    totalLabel: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      cart: null,
      subtotal: 0,
      shippingPrice: 0,
      total: 0,
    };
  },
  computed: {
    lastCartUpdate() {
      return useCartStore().lastUpdate;
    },
    isOrderPage() {
      getOrderPageContextVersion().value;
      return isOrderPageContext();
    },
    shouldShowSubtotalAndShipping() {
      const cartStore = useCartStore();
      return (
        this.showSubtotalIfFreeShipping || cartStore.cart?.shippingPrice > 0
      );
    },
    showSubtotalLine() {
      if (this.subtotalLabel) {
        return !this.subtotalEqualsTotal;
      }
      return this.shouldShowSubtotalAndShipping;
    },
    subtotalEqualsTotal() {
      const cart = this.cart;
      if (!cart) return false;
      const sub = cart.defaultSubtotal ?? cart.subtotal ?? 0;
      const tot = cart.total ?? 0;
      return sub === tot;
    },
    showShippingLine() {
      return this.shouldShowSubtotalAndShipping;
    },
    appliedCoupons() {
      if (!this.cart?.coupons) {
        return [];
      }
      const coupons = this.cart.coupons;
      const list = Array.isArray(coupons) ? coupons : Object.values(coupons);
      return list.filter(
        (coupon) =>
          coupon.isValid !== false &&
          (coupon.discountedAmount > 0 || coupon.discountAmount > 0),
      );
    },
    subtotalLabelText() {
      if (this.subtotalLabel) {
        return this.subtotalLabel;
      }
      return this.labelWithOptionalColon("labels.subTotal");
    },
    shippingLabelText() {
      return this.labelWithOptionalColon("labels.shippingFees");
    },
    discountCodeLabel() {
      return this.$t("labels.discountCode");
    },
    totalLabelText() {
      if (this.totalLabel) {
        return this.totalLabel;
      }
      return this.labelWithOptionalColon("labels.total");
    },
  },
  mounted() {
    this.cart = this.getCart();
    this.refreshAmounts();
  },
  methods: {
    getCart() {
      return useCartStore().cart;
    },
    labelWithOptionalColon(i18nKey) {
      const base = this.$t(i18nKey);
      return this.isOrderPage ? base : `${base} :`;
    },
    deleteCouponAction(coupon) {
      CouponService.deleteCoupon(coupon.code);
    },
    formatPrice(price) {
      return Price.format(price);
    },
    refreshAmounts() {
      if (!this.cart) {
        return;
      }
      this.subtotal = this.formatPrice(
        this.cart.defaultSubtotal
          ? this.cart.defaultSubtotal
          : this.cart.subtotal,
      );
      this.shippingPrice = this.formatPrice(this.cart.shippingPrice);
      this.total = this.formatPrice(this.cart.total);
    },
  },
  watch: {
    lastCartUpdate: {
      handler() {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        this.cart = this.getCart();
      },
    },
    cart: {
      handler() {
        if (!window?.globalConfig?.domainId) {
          return;
        }
        this.refreshAmounts();
      },
      deep: true,
    },
  },
};
</script>
